'use client'

import { formatFontFamily } from '@/lib/themeHelpers'
import { useScrollAnimation, scrollAnimationClasses } from '@/lib/useScrollAnimation'

interface AboutUsSectionProps {
  surface: "dark" | "light";
  theme: any;
  radius: number;
  fontFamily: string;
  gradStart: string;
  gradEnd: string;
  angle: number;
  title: string;
  description: string;
  imageUrl: string;
}

export default function AboutUsSection({
  surface,
  theme,
  radius,
  fontFamily,
  gradStart,
  gradEnd,
  angle,
  title,
  description,
  imageUrl
}: AboutUsSectionProps) {
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation();
  const { ref: imageRef, isVisible: imageVisible } = useScrollAnimation({ rootMargin: '0px 0px -100px 0px' });

  const backgroundStyle = {
    backgroundImage: surface === "dark" 
      ? `linear-gradient(${angle}deg, ${gradStart}, ${gradEnd})`
      : `linear-gradient(${angle}deg, #f8f9fa, #e9ecef)`,
    fontFamily: formatFontFamily(fontFamily)
  };

  return (
    <section id="aboutus" className="relative w-full" style={backgroundStyle}>
      <div className="mx-auto max-w-6xl px-6 py-16 lg:py-24 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          {/* Content */}
          <div 
            ref={contentRef as React.RefObject<HTMLDivElement>}
            className={`${scrollAnimationClasses.transition} ${contentVisible ? scrollAnimationClasses.visible : scrollAnimationClasses.hidden}`}
          >
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-6 leading-tight font-primary" style={{ color: theme.text }}>
              {title}
            </h2>
            <p className="text-lg leading-relaxed font-secondary" style={{ color: theme.mutedText }}>
              {description}
            </p>
          </div>

          {/* Image */}
          <div 
            ref={imageRef as React.RefObject<HTMLDivElement>}
            className={`${scrollAnimationClasses.transition} ${imageVisible ? scrollAnimationClasses.visible : scrollAnimationClasses.hidden}`}
            style={{ transitionDelay: '200ms' }}
          >
            {imageUrl ? (
              <div 
                className="w-full h-full min-h-[320px] max-h-[400px] lg:max-h-[450px] overflow-hidden shadow-xl transition-shadow duration-300 hover:shadow-2xl group"
                style={{ borderRadius: radius }}
              >
                <img 
                  src={imageUrl} 
                  alt="About Us" 
                  className="w-full min-h-[320px] max-h-[400px] lg:max-h-[450px] object-cover object-[center_70%] transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            ) : (
              <div 
                className="w-full h-80 lg:h-96 flex items-center justify-center text-center"
                style={{
                  borderRadius: radius,
                  backgroundColor: theme.card,
                  border: `2px dashed ${theme.border}`,
                }}
              >
                <div>
                  <div className="mb-4">
                    <svg className="w-20 h-20 mx-auto opacity-50" fill="currentColor" style={{ color: theme.subtleText }} viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-lg font-medium font-primary" style={{ color: theme.subtleText }}>
                    About Us Image
                  </p>
                  <p className="text-sm mt-2 font-primary" style={{ color: theme.subtleText }}>
                    Add an image to showcase your company
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
